import { Chart as ChartJs, ChartTypeRegistry, Plugin } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import chroma from 'chroma-js';
import history from 'history.js';
import React, { useMemo, useRef, useState } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Card, CardBody, CardHeader, CardTitle } from 'reactstrap';

import {
  buildCurrentFindingsDatasets,
  CurrentFindingsDataset,
  getAggregateCounts,
} from './chartData';
import {
  BAR_CHART_OPTIONS,
  DOUGHNUT_CHART_OPTIONS,
} from '../../../components/BarOrDoughnutChart/options';
import { ChartType } from '../../../types';

import 'views/Admin/Dashboard/styles.css';

interface Props {
  chartType: ChartType;
  title: string;
  labels: string[];
  datasets: CurrentFindingsDataset[];
  includedLabels: string[];
  labelColors: string[];
  getDrillDownUrl: (label: string, provider?: string) => string;
}

const DoughnutChartDataLabels = ChartDataLabels as Plugin<'doughnut'>;

const CurrentFindingsChart = ({
  chartType,
  title,
  labels,
  datasets,
  includedLabels,
  labelColors,
  getDrillDownUrl,
}: Props) => {
  const barChartReference = useRef<ChartJs<'bar'> | null>(null);
  const doughnutChartReference = useRef<ChartJs<'doughnut'> | null>(null);
  const [hiddenIndexes, setHiddenIndexes] = useState<number[]>([]);

  const displayedDatasets = useMemo(
    () => buildCurrentFindingsDatasets(datasets),
    [datasets],
  );
  const aggregateCounts = useMemo(
    () => getAggregateCounts(displayedDatasets),
    [displayedDatasets],
  );

  const headlineCount = useMemo(() => {
    return aggregateCounts.reduce((sum, count, index) => {
      const isIncluded = includedLabels.includes(labels[index]);
      const isHidden =
        chartType === ChartType.Doughnut && hiddenIndexes.includes(index);
      return isIncluded && !isHidden ? sum + count : sum;
    }, 0);
  }, [aggregateCounts, chartType, hiddenIndexes, includedLabels, labels]);

  const visibleDatasets = useMemo(
    () =>
      displayedDatasets.map((dataset) => ({
        ...dataset,
        data: dataset.data.map((count, index) =>
          chartType === ChartType.Doughnut && hiddenIndexes.includes(index)
            ? 0
            : count,
        ),
      })),
    [chartType, displayedDatasets, hiddenIndexes],
  );

  const doughnutOptions = useMemo(
    () => ({
      ...DOUGHNUT_CHART_OPTIONS,
      plugins: {
        ...DOUGHNUT_CHART_OPTIONS.plugins,
        legend: {
          ...DOUGHNUT_CHART_OPTIONS.plugins?.legend,
          onClick: (_event, legendItem, legend) => {
            if (legendItem.index === undefined) return;
            const index = legendItem.index;
            legend.chart.toggleDataVisibility(index);
            legend.chart.update();
            setHiddenIndexes((current) =>
              current.includes(index)
                ? current.filter((item) => item !== index)
                : [...current, index],
            );
          },
        },
      },
    }),
    [],
  );

  const chartData = useMemo(
    () => ({
      labels,
      datasets: visibleDatasets.map((dataset) => ({
        label: dataset.label,
        fill: true,
        backgroundColor: labelColors,
        hoverBorderColor: labelColors.map((color) =>
          chroma(color).darken(2).css(),
        ),
        data: dataset.data,
      })),
    }),
    [labelColors, labels, visibleDatasets],
  );

  const handleChartClick = <TType extends keyof ChartTypeRegistry>(
    event,
    chart: ChartJs<TType> | null,
  ) => {
    if (!chart) return;
    const activeElements = chart.getElementsAtEventForMode(
      event,
      'nearest',
      { intersect: true },
      false,
    );
    if (activeElements.length === 0) return;

    const activeElement = activeElements[0];
    const label = labels[activeElement.index];
    const provider = displayedDatasets[activeElement.datasetIndex]?.provider;
    const url = getDrillDownUrl(label, provider);
    if (url) history.push(url);
  };

  return (
    <Card className="card-chart border border-light">
      <CardHeader>
        <CardTitle tag="div" className="mb-0">
          <div className="d-flex flex-row align-items-baseline">
            <h1 className="d-inline mb-0">{headlineCount}</h1>
            <h5 className="card-category px-1">{title}</h5>
          </div>
        </CardTitle>
      </CardHeader>
      <CardBody style={{ cursor: 'pointer' }}>
        <div className="chart-area">
          {chartType === ChartType.Doughnut ? (
            <Doughnut
              ref={(node) => {
                if (node != null) doughnutChartReference.current = node;
              }}
              data={chartData}
              options={doughnutOptions}
              plugins={[DoughnutChartDataLabels]}
              onClick={(event) =>
                handleChartClick(event, doughnutChartReference.current)
              }
            />
          ) : (
            <Bar
              ref={(node) => {
                if (node != null) barChartReference.current = node;
              }}
              data={chartData}
              options={BAR_CHART_OPTIONS}
              plugins={[ChartDataLabels]}
              onClick={(event) =>
                handleChartClick(event, barChartReference.current)
              }
            />
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export default CurrentFindingsChart;
